import useTranslation from 'next-translate/useTranslation';
import { Box, Stack } from '@mantine/core';
import { match } from 'ts-pattern';

import { CurrencyConvertor } from '~/components/utils/formatters';
import { toSafeMarkup } from '~/domains/common/utils/toSafeMarkup';
import { Text } from '~/domains/design-system/Text';
import Accordion from '~/domains/design-system/components/Accordion/Accordion';
import AccordionButton from '~/domains/design-system/components/Accordion/AccordionButton';

import type { LoyaltyTier, RewardBasis } from '../../../../types/gamification.types';
import { TIER_COLORS } from '../../constants';

import { useStyles } from './WhalersClubFaqAccordion.styles';

interface WhalersClubFaqAccordionProps {
  rewardBasis: RewardBasis;
  tiers: LoyaltyTier[];
  faq: string | null;
  whatQualifies: string;
  whatDoesNotQualify: string;
  isDarkMode?: boolean;
  handleOpenHistoryModal: () => void;
}

function formatRange(min: number, max: number | null): string {
  const minStr = CurrencyConvertor(min);
  if (max === null || max === undefined) return `${minStr}+`;
  const maxStr = CurrencyConvertor(max);
  return `${minStr} - ${maxStr}`;
}

function WhalersClubFaqAccordion({
  rewardBasis,
  isDarkMode,
  tiers,
  faq,
  whatQualifies,
  whatDoesNotQualify,
  handleOpenHistoryModal,
}: WhalersClubFaqAccordionProps) {
  const { classes, cx } = useStyles({ isDarkMode });
  const { t } = useTranslation('loyalty');

  const reversedTiers = tiers.toReversed();
  const { tierRewardKey, showsCreditsRange } = match(rewardBasis)
    .with('handle', () => ({
      tierRewardKey: 'whalersClub.faqAccordion.tierRewardLabel' as const,
      showsCreditsRange: true,
    }))
    .with('net_loss', () => ({
      tierRewardKey: 'whalersClub.faqAccordion.tierRewardLabelLossBack' as const,
      showsCreditsRange: false,
    }))
    .exhaustive();
  const style = isDarkMode ? { isDark: true } : { isGrey: true };

  return (
    <Stack /* eslint-disable-line local-rules/use-theme-tokens */ spacing={16}>
      <div>
        {faq && (
          <Accordion
            name="how-it-works"
            title={t('whalersClub.faqAccordion.howItWorks')}
            {...style}
          >
            <Text
              variant="body-medium"
              className={classes.content}
              dangerouslySetInnerHTML={{ __html: toSafeMarkup(faq, { useMarkdown: true }) }}
            />
          </Accordion>
        )}
        <Accordion name="tiers" title={t('whalersClub.faqAccordion.whalersClubTiers')} {...style}>
          <Box className={cx(classes.content)}>
            {reversedTiers.map((tier) => {
              const rewardPercent = tier.rewardRates[0]?.rateValue;
              const tierColor = TIER_COLORS[tier.name].secondary;

              return (
                <div key={tier.id} className={classes.tierRow}>
                  <div className={cx(classes.tierSwatch)} style={{ background: tierColor }} />
                  <div className={classes.tierInfo}>
                    <div className={classes.tierNameRow}>
                      <Text variant="headline-medium">{tier.name.toUpperCase()}</Text>
                      {rewardPercent !== undefined && (
                        <Text variant="headline-small" color={tierColor}>
                          {t(tierRewardKey, {
                            percent: Math.round(rewardPercent * 100),
                          })}
                        </Text>
                      )}
                    </div>
                    <Text variant="title-small" display="block">
                      {t('whalersClub.faqAccordion.weeklyEntryFees', {
                        range: formatRange(Math.ceil(tier.minValue), tier.maxValue),
                      })}
                    </Text>
                    {showsCreditsRange && (
                      <Text variant="title-small" display="block">
                        {t('whalersClub.faqAccordion.splashCreditsRange', {
                          range: formatRange(
                            Math.ceil(tier.minValue * rewardPercent),
                            tier.maxValue * rewardPercent || null
                          ),
                        })}
                      </Text>
                    )}
                  </div>
                </div>
              );
            })}
          </Box>
        </Accordion>
        {whatQualifies && (
          <Accordion name="what-counts" title={t('whalersClub.faqAccordion.whatCounts')} {...style}>
            <Text
              variant="body-medium"
              className={classes.content}
              dangerouslySetInnerHTML={{
                __html: toSafeMarkup(whatQualifies, { useMarkdown: true }),
              }}
            />
          </Accordion>
        )}
        {whatDoesNotQualify && (
          <Accordion
            name="what-does-not-count"
            title={t('whalersClub.faqAccordion.whatDoesNotCount')}
            {...style}
          >
            <Text
              variant="body-medium"
              className={classes.content}
              dangerouslySetInnerHTML={{
                __html: toSafeMarkup(whatDoesNotQualify, { useMarkdown: true }),
              }}
            />
          </Accordion>
        )}
      </div>
      <div>
        <AccordionButton
          title={t('whalersClub.faqAccordion.yourRewardsHistory')}
          onClick={handleOpenHistoryModal}
          showArrow
          {...style}
        />
      </div>
    </Stack>
  );
}

export default WhalersClubFaqAccordion;
